import React, { useEffect, useRef, useState, useCallback } from 'react';
import { renderAsync } from 'docx-preview';
import { getUser, getLocalKey } from '../../../../../utils/commonfunction';
import { ROLE } from '../../../../../utils/Constants';

// clubId     → preview a club's signed agreement document (/render-docx/:clubId)
// templateId → preview a master template file         (/render-docx?templateId=...)
const NativeDocxViewer = ({ clubId, templateId, customUrl }) => {
    const wrapperRef = useRef(null);
    const containerRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Zoom & Scaling state
    const [zoomMode, setZoomMode] = useState('fit'); // 'fit' | 'manual'
    const [manualScale, setManualScale] = useState(1);
    const [containerWidth, setContainerWidth] = useState(800);
    const [docNaturalWidth, setDocNaturalWidth] = useState(816);

    const updateDimensions = useCallback(() => {
        if (!wrapperRef.current) return;
        const currentWrapperWidth = wrapperRef.current.clientWidth || 360;
        setContainerWidth(currentWrapperWidth);

        if (containerRef.current) {
            const firstSection = containerRef.current.querySelector('section.docx');
            if (firstSection) {
                const natWidth = firstSection.offsetWidth || 816;
                setDocNaturalWidth(natWidth);
            }
        }
    }, []);

    // ResizeObserver for dynamic responsive width detection
    useEffect(() => {
        if (!wrapperRef.current) return;
        
        updateDimensions();

        const ro = new ResizeObserver(() => {
            updateDimensions();
        });

        ro.observe(wrapperRef.current);
        window.addEventListener('resize', updateDimensions);

        return () => {
            ro.disconnect();
            window.removeEventListener('resize', updateDimensions);
        };
    }, [updateDimensions]);

    useEffect(() => {
        let isMounted = true;

        const loadDocument = async () => {
            try {
                setLoading(true);
                setError(null);

                const rawApi = process.env.REACT_APP_API_URL || "http://localhost:7000";
                let cleanBase = rawApi.endsWith("/") ? rawApi.slice(0, -1) : rawApi;
                if (!cleanBase.includes("/webservice/api/v1")) {
                    cleanBase = `${cleanBase}/webservice/api/v1`;
                }

                let fetchUrl = customUrl;
                if (!fetchUrl) {
                    if (templateId) {
                        fetchUrl = `${cleanBase}/admin/cms/agreements/render-docx?templateId=${templateId}`;
                    } else if (clubId) {
                        fetchUrl = `${cleanBase}/admin/cms/agreements/render-docx/${clubId}`;
                    } else {
                        fetchUrl = `${cleanBase}/club-admin/billing/render-docx`;
                    }
                }

                // Retrieve token reliably for any role
                let token = "";
                try {
                    const rolesToTry = [
                        ROLE.SUPER_ADMIN,
                        ROLE.SUB_ADMIN,
                        ROLE.CLUB_ADMIN,
                        "super_admin",
                        "sub_admin",
                        "club_admin"
                    ];
                    for (const r of rolesToTry) {
                        const uData = getUser(r);
                        if (uData && uData.token) {
                            token = uData.token;
                            break;
                        }
                        const raw = getLocalKey(r) || localStorage.getItem(r);
                        if (raw) {
                            try {
                                const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                                if (parsed && parsed.token) {
                                    token = parsed.token;
                                    break;
                                }
                            } catch (e) {
                                if (typeof raw === "string" && raw.length > 20) {
                                    token = raw;
                                    break;
                                }
                            }
                        }
                    }
                } catch (e) { }

                const headers = {};
                if (token) {
                    const cleanTok = typeof token === "string" ? token.trim() : token;
                    headers["Authorization"] = `Bearer ${cleanTok}`;
                }

                const response = await fetch(fetchUrl, { headers });
                if (!response.ok) {
                    throw new Error(`Failed to load document (${response.status} ${response.statusText})`);
                }

                const blob = await response.blob();

                if (isMounted && containerRef.current) {
                    containerRef.current.innerHTML = "";
                    await renderAsync(blob, containerRef.current, null, {
                        className: "docx-container",
                        inWrapper: true,
                        ignoreWidth: false,
                        ignoreHeight: false,
                        experimental: true,
                        useBase64URL: true,
                    });

                    const enhanceSignatureStyles = () => {
                        if (!containerRef.current) return;
                        try {
                            const elements = containerRef.current.querySelectorAll('p, span, td, div');
                            elements.forEach(el => {
                                const style = el.getAttribute('style') || '';
                                const txt = (el.textContent || '').trim();
                                const hasFairwater = style.toLowerCase().includes('fairwater') || el.style.fontFamily?.toLowerCase().includes('fairwater');
                                if (hasFairwater || txt === 'tolatunde' || (txt.toLowerCase() === 'tolatunde')) {
                                    el.style.setProperty('font-family', "'Fairwater Script', 'Fairwater Script Regular', 'Great Vibes', 'Alex Brush', 'Dancing Script', 'Parisienne', cursive", 'important');
                                    el.style.setProperty('font-size', '28px', 'important');
                                    el.style.setProperty('color', '#156082', 'important');
                                    el.style.setProperty('letter-spacing', '0.5px', 'important');
                                    el.style.setProperty('font-style', 'normal', 'important');
                                    el.style.setProperty('line-height', '1.2', 'important');
                                    el.style.setProperty('vertical-align', 'middle', 'important');
                                }
                            });
                        } catch (e) {}
                    };

                    const fixDarkBackgroundContrast = () => {
                        if (!containerRef.current) return;
                        try {
                            const elements = containerRef.current.querySelectorAll('p, td, th, h1, h2, h3, h4, h5, h6, tr, div');
                            elements.forEach(el => {
                                if (el.tagName === 'SECTION' || el.classList.contains('docx') || el.classList.contains('docx-wrapper')) return;

                                const bg = window.getComputedStyle(el).backgroundColor;
                                if (bg && bg !== 'transparent' && bg !== 'rgba(0, 0, 0, 0)') {
                                    const rgb = bg.match(/\d+/g);
                                    if (rgb && rgb.length >= 3) {
                                        const r = parseInt(rgb[0], 10);
                                        const g = parseInt(rgb[1], 10);
                                        const b = parseInt(rgb[2], 10);
                                        const a = rgb.length >= 4 ? parseFloat(rgb[3]) : 1;
                                        if (a > 0.5) {
                                            const luminance = (r * 299 + g * 587 + b * 114) / 1000;
                                            if (luminance < 110) {
                                                el.style.setProperty('color', '#ffffff', 'important');
                                                el.querySelectorAll('span, strong, b, em, i, a').forEach(child => {
                                                    child.style.setProperty('color', '#ffffff', 'important');
                                                });
                                            }
                                        }
                                    }
                                }
                                if (el.classList.contains('docx_box')) {
                                    el.style.setProperty('color', '#ffffff', 'important');
                                    el.querySelectorAll('span, strong, b, em, i, a').forEach(child => {
                                        child.style.setProperty('color', '#ffffff', 'important');
                                    });
                                }
                            });
                        } catch (e) {}
                    };

                    fixDarkBackgroundContrast();
                    enhanceSignatureStyles();

                    // Update natural document dimensions and re-verify contrast after styles settle
                    setTimeout(() => {
                        if (isMounted) {
                            fixDarkBackgroundContrast();
                            enhanceSignatureStyles();
                            updateDimensions();
                        }
                    }, 100);
                }
            } catch (err) {
                console.error("[NativeDocxViewer] Render Error:", err);
                if (isMounted) {
                    setError(err.message || "Failed to render document template.");
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        loadDocument();

        return () => {
            isMounted = false;
        };
    }, [clubId, templateId, customUrl, updateDimensions]);

    // Calculate effective scale
    const fitScale = Math.min(1, Math.max(0.2, (containerWidth - 8) / (docNaturalWidth || 816)));
    const currentScale = zoomMode === 'fit' ? fitScale : manualScale;

    const handleZoomIn = () => {
        setZoomMode('manual');
        setManualScale(prev => Math.min(2.0, +(prev + 0.15).toFixed(2)));
    };

    const handleZoomOut = () => {
        setZoomMode('manual');
        setManualScale(prev => Math.max(0.3, +(prev - 0.15).toFixed(2)));
    };

    const handleReset100 = () => {
        setZoomMode('manual');
        setManualScale(1.0);
    };

    const handleToggleFit = () => {
        if (zoomMode === 'fit') {
            setZoomMode('manual');
            setManualScale(1.0);
        } else {
            setZoomMode('fit');
        }
    };

    const isScaledDown = currentScale < 0.999;

    return (
        <div ref={wrapperRef} className="docx-viewer-wrapper w-100" style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
            <style>{`
                @font-face {
                    font-family: 'Fairwater Script';
                    src: local('Fairwater Script Regular'), local('Fairwater Script'), local('FairwaterScript-Regular'), local('FairwaterScript'),
                         url('https://fonts.gstatic.com/s/greatvibes/v19/RWmAnKFTuznMhvMl5F4JMDXvWam4PIA3_A.woff2') format('woff2');
                    font-weight: normal;
                    font-style: normal;
                }
                @font-face {
                    font-family: 'Fairwater Script Regular';
                    src: local('Fairwater Script Regular'), local('Fairwater Script'), local('FairwaterScript-Regular'), local('FairwaterScript'),
                         url('https://fonts.gstatic.com/s/greatvibes/v19/RWmAnKFTuznMhvMl5F4JMDXvWam4PIA3_A.woff2') format('woff2');
                    font-weight: normal;
                    font-style: normal;
                }
                @font-face {
                    font-family: 'Fairwater';
                    src: local('Fairwater Script Regular'), local('Fairwater Script'), local('FairwaterScript-Regular'), local('FairwaterScript'),
                         url('https://fonts.gstatic.com/s/greatvibes/v19/RWmAnKFTuznMhvMl5F4JMDXvWam4PIA3_A.woff2') format('woff2');
                    font-weight: normal;
                    font-style: normal;
                }

                .docx-viewer-wrapper {
                    width: 100% !important;
                    max-width: 100% !important;
                    box-sizing: border-box !important;
                }
                .docx-viewer-toolbar {
                    background-color: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 6px 12px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 12px;
                    flex-wrap: wrap;
                    gap: 8px;
                }
                .docx-viewer-wrapper .docx-wrapper {
                    background: transparent !important;
                    padding: 0 !important;
                    display: flex !important;
                    flex-direction: column !important;
                    align-items: center !important;
                    box-sizing: border-box !important;
                    width: 100% !important;
                }
                .docx-viewer-wrapper .docx-wrapper > section.docx {
                    box-sizing: border-box !important;
                    margin: 0 auto 24px auto !important;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08) !important;
                    border-radius: 8px !important;
                    border: 1px solid #e2e8f0 !important;
                    background: #ffffff !important;
                    overflow: visible !important;
                }
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="Fairwater"],
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="fairwater"],
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="Fairwater Script"],
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="Fairwater Script Regular"],
                [style*="Fairwater Script"],
                [style*="Fairwater"] {
                    font-family: 'Fairwater Script', 'Fairwater Script Regular', 'Great Vibes', 'Alex Brush', 'Dancing Script', 'Parisienne', cursive !important;
                    font-size: 28px !important;
                    font-weight: 500 !important;
                    letter-spacing: 0.5px !important;
                    line-height: 1.2 !important;
                    color: #156082 !important;
                    font-style: normal !important;
                    display: inline-block !important;
                    vertical-align: middle !important;
                }
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="color: rgb(255, 0, 0)"],
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="color: #ff0000"],
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="color: #FF0000"],
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="color: red"],
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="color: #c00000"],
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="color: #C00000"],
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="color: rgb(192, 0, 0)"],
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="color: #ed1c24"],
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="color: #ED1C24"],
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="color: #e00000"] {
                    color: #000000 !important;
                }
                .docx-viewer-wrapper .docx-wrapper > section.docx .docx_box,
                .docx-viewer-wrapper .docx-wrapper > section.docx .docx_box *,
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="background-color: rgb(0, 29, 61)"],
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="background-color: #001D3D"],
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="background-color: #001d3d"],
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="background: rgb(0, 29, 61)"],
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="background: #001D3D"],
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="background: #001d3d"],
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="background-color: rgb(0, 32, 96)"],
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="background-color: #002060"],
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="background: #002060"],
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="background-color: rgb(11, 37, 69)"],
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="background-color: #0B2545"],
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="background-color: #0b2545"] {
                    color: #ffffff !important;
                }
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="background-color: rgb(0, 29, 61)"] *,
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="background-color: #001D3D"] *,
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="background-color: #001d3d"] *,
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="background: rgb(0, 29, 61)"] *,
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="background: #001D3D"] *,
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="background: #001d3d"] *,
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="background-color: rgb(0, 32, 96)"] *,
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="background-color: #002060"] *,
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="background: #002060"] *,
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="background-color: rgb(11, 37, 69)"] *,
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="background-color: #0B2545"] *,
                .docx-viewer-wrapper .docx-wrapper > section.docx [style*="background-color: #0b2545"] * {
                    color: #ffffff !important;
                }
                .docx-viewport-scroll-area {
                    width: 100%;
                    max-width: 100%;
                    overflow-x: auto;
                    overflow-y: visible;
                    -webkit-overflow-scrolling: touch;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    box-sizing: border-box;
                    padding: 4px 0;
                }
            `}</style>

            {/* Document Controls Toolbar */}
            {!loading && !error && (
                <div className="docx-viewer-toolbar">
                    <div className="d-flex align-items-center gap-2">
                        <span className="badge badge-light text-muted border d-inline-flex align-items-center" style={{ fontSize: 11, padding: "4px 8px" }}>
                            <i className="fa fa-file-word-o text-primary me-1"></i> Word (.docx)
                        </span>
                        {isScaledDown && zoomMode === 'fit' && (
                            <span className="text-muted tx-11 d-none d-sm-inline">
                                Multi-page agreement (Auto-fit)
                            </span>
                        )}
                    </div>
                    <div className="d-flex align-items-center gap-1">
                        <button
                            type="button"
                            className={`btn btn-xs ${zoomMode === 'fit' ? 'btn-primary text-white' : 'btn-outline-secondary'}`}
                            onClick={handleToggleFit}
                            title="Auto Fit to Screen Width"
                            style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4 }}
                        >
                            <i className="fa fa-expand me-1"></i> {zoomMode === 'fit' ? "Fit: ON" : "Fit Width"}
                        </button>
                        <button
                            type="button"
                            className="btn btn-xs btn-outline-secondary"
                            onClick={handleZoomOut}
                            title="Zoom Out"
                            style={{ fontSize: 11, padding: "3px 7px", borderRadius: 4 }}
                        >
                            <i className="fa fa-minus"></i>
                        </button>
                        <button
                            type="button"
                            className="btn btn-xs btn-outline-secondary"
                            onClick={handleReset100}
                            title="Reset to 100% Size"
                            style={{ fontSize: 11, minWidth: 46, padding: "3px 6px", fontWeight: 700, borderRadius: 4 }}
                        >
                            {Math.round(currentScale * 100)}%
                        </button>
                        <button
                            type="button"
                            className="btn btn-xs btn-outline-secondary"
                            onClick={handleZoomIn}
                            title="Zoom In"
                            style={{ fontSize: 11, padding: "3px 7px", borderRadius: 4 }}
                        >
                            <i className="fa fa-plus"></i>
                        </button>
                    </div>
                </div>
            )}

            {loading && (
                <div className="d-flex flex-column align-items-center justify-content-center p-4 p-sm-5 text-center" style={{ color: "#007d7f" }}>
                    <div className="spinner-border text-teal mb-3" role="status" style={{ width: "2.5rem", height: "2.5rem" }} />
                    <p className="font-weight-bold tx-13 tx-sm-14">Rendering exact 1:1 Word (.docx) document template...</p>
                </div>
            )}

            {error && (
                <div className="alert alert-danger text-center max-w-lg my-4 mx-auto p-3 p-sm-4" style={{ borderRadius: 8 }}>
                    <p className="font-weight-bold mb-1">Document Preview Error</p>
                    <p className="tx-12 mb-0">{error}</p>
                </div>
            )}

            {/* Scrollable Multi-Page Document Viewport */}
            <div className="docx-viewport-scroll-area">
                <div
                    ref={containerRef}
                    style={{
                        width: `${docNaturalWidth || 816}px`,
                        maxWidth: currentScale >= 1 ? "100%" : undefined,
                        zoom: currentScale < 1 ? currentScale : undefined,
                        transform: typeof CSS !== 'undefined' && CSS.supports && !CSS.supports('zoom', '1') && currentScale < 1 ? `scale(${currentScale})` : undefined,
                        transformOrigin: "top center",
                        transition: "zoom 0.2s ease, transform 0.2s ease",
                        minHeight: loading ? "0px" : "300px",
                        display: loading ? "none" : "block",
                        margin: "0 auto"
                    }}
                />
            </div>
        </div>
    );
};

export default NativeDocxViewer;
